import React, { forwardRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import LoadingButton from "@mui/lab/LoadingButton";

const SFAsyncActionButton = forwardRef(
  ({ children, onClick, onError, variant = "primary", ...props }, ref) => {
    const [loading, setLoading] = useState(false);
    let isMounted = true; // Flag to track the mounted state of the component
    useEffect(() => {
      // Set `isMounted` to false when the component unmounts
      return () => {
        isMounted = false;
      };
    }, []);

    const handleClick = async (...args) => {
      setLoading(true);
      try {
        await onClick(...args);
      } catch (error) {
        if (onError && typeof onError === "function") {
          onError(error);
        }
      } finally {
        // `isMounted` is checked before calling `setLoading` to avoid memory leaks
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    return (
      <LoadingButton
        ref={ref}
        onClick={handleClick}
        loading={loading}
        variant={variant}
        {...props}
      >
        {/* Wrap children in a span to avoid the following issue: https://github.com/mui/material-ui/issues/27853 */}
        <span>{children}</span>
      </LoadingButton>
    );
  },
);

export default SFAsyncActionButton;

/**
 * Prop types for the `SFAsyncActionButton component`. `Button` props are also available.
 */
SFAsyncActionButton.propTypes = {
  /**
   * The content of the button.
   */
  children: PropTypes.node.isRequired,
  /**
   * The async function to be called when the button is clicked.
   */
  onClick: PropTypes.func.isRequired,
  /**
   * The function to be called when the promise is rejected. Receives the error as an argument.
   */
  onError: PropTypes.func,
  /**
   * Determines the style of the button.
   */
  variant: PropTypes.oneOf(["primary", "secondary", "utility"]),
};
